import React from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, XCircle, Mail, ShieldCheck } from "lucide-react";

export default function RefundPolicy() {
  return (
    <div className="min-h-screen bg-slate-100 font-sans text-slate-800 relative overflow-x-hidden flex flex-col justify-between selection:bg-blue-200">
      
      {/* AMBIENT BACKGROUND GLOW ORBS */}
      <div className="fixed top-10 left-10 w-96 h-96 bg-blue-500/10 rounded-full blur-[120px] pointer-events-none"></div>
      <div className="fixed bottom-10 right-10 w-96 h-96 bg-indigo-500/10 rounded-full blur-[120px] pointer-events-none"></div>

      {/* MAIN LAYOUT */}
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8 w-full flex-grow grid grid-cols-1 lg:grid-cols-4 gap-8 relative z-10">
        
        {/* LEFT FIXED NAVIGATION SIDEBAR */}
        <aside className="lg:col-span-1 space-y-6">
          <Link to="/" className="inline-block px-2">
            <span className="text-2xl font-black tracking-tight text-slate-900">
              Expo<span className="text-blue-600">Lead</span>
            </span>
          </Link>

          <nav className="flex flex-col gap-2 bg-white/70 backdrop-blur-md p-3 rounded-3xl border border-white/80 shadow-sm">
            <Link
              to="/help"
              className="px-4 py-3 text-sm font-semibold text-slate-600 hover:text-slate-900 hover:bg-slate-100/70 rounded-2xl transition"
            >
              Help Center
            </Link>
            <Link
              to="/privacy-policy"
              className="px-4 py-3 text-sm font-semibold text-slate-600 hover:text-slate-900 hover:bg-slate-100/70 rounded-2xl transition"
            >
              Privacy Policy
            </Link>
            <Link
              to="/terms-and-conditions"
              className="px-4 py-3 text-sm font-semibold text-slate-600 hover:text-slate-900 hover:bg-slate-100/70 rounded-2xl transition"
            >
              Terms of Service
            </Link>
            <div className="px-5 py-3 text-sm font-bold bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-2xl shadow-md shadow-blue-600/25">
              Refund Policy
            </div>
            <Link
              to="/about"
              className="px-4 py-3 text-sm font-semibold text-slate-600 hover:text-slate-900 hover:bg-slate-100/70 rounded-2xl transition"
            >
              About Us
            </Link>
          </nav>
        </aside>

        {/* RIGHT CONTENT AREA */}
        <main className="lg:col-span-3">
          <div className="bg-white/90 backdrop-blur-2xl border border-white shadow-2xl shadow-slate-300/40 rounded-[2.5rem] p-6 sm:p-10 space-y-8">
            
            {/* HEADER TITLE */}
            <div className="border-b border-slate-100 pb-6 space-y-2">
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-600 text-xs font-bold">
                <ShieldCheck className="w-3.5 h-3.5" /> Razorpay Verified Payment Terms
              </div>
              <h1 className="text-3xl sm:text-4xl font-black text-slate-900 tracking-tight">
                ExpoLead Payment, Cancellation, and Refund Policy
              </h1>
              <p className="text-xs font-semibold text-slate-400">
                Last Updated: July 2026
              </p>
            </div>

            <p className="text-sm text-slate-600 leading-relaxed font-medium bg-slate-50 p-4 rounded-2xl border border-slate-200/60">
              This policy outlines the terms and conditions regarding purchases, cancellations, and refunds for credits and subscriptions on the ExpoLead platform. By initiating a purchase, you agree to the terms laid out below.
            </p>

            {/* SECTION 1 */}
            <section className="space-y-3">
              <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                <span className="w-7 h-7 rounded-xl bg-blue-500/10 text-blue-600 flex items-center justify-center font-black text-xs">1</span>
                Pricing and Payments
              </h2>
              <ul className="list-disc pl-9 space-y-2 text-sm text-slate-600 leading-relaxed">
                <li>All transactions on ExpoLead are processed securely through our payment gateway partner, <strong>Razorpay</strong>.</li>
                <li>All prices are listed in Indian Rupees (INR) and are inclusive of applicable taxes unless stated otherwise.</li>
                <li>ExpoLead does not capture, store, or process your credit card, debit card, or net banking details on our servers. All sensitive payment data is handled directly by Razorpay, which is strictly PCI-DSS compliant.</li>
                <li>Upon successful payment, your purchased credits (e.g., 100, 300, or 1000 credits) will be instantly credited to your ExpoLead account.</li>
              </ul>
            </section>

            {/* SECTION 2 */}
            <section className="space-y-3">
              <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                <span className="w-7 h-7 rounded-xl bg-blue-500/10 text-blue-600 flex items-center justify-center font-black text-xs">2</span>
                Credit Usage and Expiration
              </h2>
              <ul className="list-disc pl-9 space-y-2 text-sm text-slate-600 leading-relaxed">
                <li>Purchased credits are non-transferable and cannot be exchanged for cash.</li>
                <li>Credits are permanently tied to the account that initiated the purchase.</li>
                <li>Unless specifically stated in a promotional offer, standard purchased scan credits do not expire as long as your ExpoLead account remains active.</li>
                <li>If an account is permanently deleted or deactivated for more than 30 days, any remaining credits will be forfeited.</li>
              </ul>
            </section>

            {/* SECTION 3 */}
            <section className="space-y-3">
              <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                <span className="w-7 h-7 rounded-xl bg-blue-500/10 text-blue-600 flex items-center justify-center font-black text-xs">3</span>
                Cancellation Policy
              </h2>
              <div className="pl-9 space-y-3 text-sm text-slate-600">
                <div className="p-4 bg-slate-50 border border-slate-200/80 rounded-2xl space-y-1">
                  <h3 className="font-bold text-slate-900 text-xs uppercase tracking-wider">Pay-as-you-go Credits</h3>
                  <p>Purchases of individual credit packs (e.g., ₹499, ₹1299, ₹2999) are one-time transactions. There are no recurring charges to cancel.</p>
                </div>
                <div className="p-4 bg-slate-50 border border-slate-200/80 rounded-2xl space-y-1">
                  <h3 className="font-bold text-slate-900 text-xs uppercase tracking-wider">Account Deletion</h3>
                  <p>Deleting your account will result in the immediate loss of access to all saved leads, configurations, and remaining credit balances.</p>
                </div>
                <div className="p-4 bg-slate-50 border border-slate-200/80 rounded-2xl space-y-1">
                  <h3 className="font-bold text-slate-900 text-xs uppercase tracking-wider">Subscriptions (If Applicable)</h3>
                  <p>If you are enrolled in a recurring subscription plan, you may cancel your subscription at any time. The cancellation will take effect at the end of your current billing cycle, preventing future charges. You will continue to have access to your premium features until the cycle ends.</p>
                </div>
              </div>
            </section>

            {/* SECTION 4 */}
            <section className="space-y-4">
              <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                <span className="w-7 h-7 rounded-xl bg-blue-500/10 text-blue-600 flex items-center justify-center font-black text-xs">4</span>
                Refund Policy
              </h2>
              
              <div className="pl-9 space-y-4 text-sm text-slate-600">
                <p>
                  Due to the digital nature of ExpoLead's services and the immediate allocation of scan credits, all sales are generally considered final. However, we are committed to ensuring a fair experience for our users.
                </p>

                <div className="p-4 bg-emerald-500/5 border border-emerald-500/20 rounded-2xl space-y-2">
                  <h3 className="font-bold text-emerald-700 text-xs flex items-center gap-1.5 uppercase tracking-wider">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                    Refunds will ONLY be processed under the following circumstances:
                  </h3>
                  <ul className="list-disc pl-5 space-y-1.5 text-slate-700">
                    <li><strong>Payment Failures:</strong> If your payment is deducted from your bank account but the credits are not added to your ExpoLead account due to a technical glitch, the payment gateway will automatically initiate a refund. This typically reflects in your source account within 5 to 7 business days.</li>
                    <li><strong>Duplicate Charges:</strong> If a system error results in you being charged multiple times for a single transaction, we will fully refund the duplicate amount.</li>
                    <li><strong>Service Unavailability:</strong> If you are unable to use your purchased credits due to a prolonged, verifiable outage of the ExpoLead platform lasting more than 48 hours.</li>
                  </ul>
                </div>

                <div className="p-4 bg-rose-500/5 border border-rose-500/20 rounded-2xl space-y-2">
                  <h3 className="font-bold text-rose-700 text-xs flex items-center gap-1.5 uppercase tracking-wider">
                    <XCircle className="w-4 h-4 text-rose-600" />
                    Refunds will NOT be granted for:
                  </h3>
                  <ul className="list-disc pl-5 space-y-1.5 text-slate-700">
                    <li>Credits that have already been partially or fully consumed (e.g., used to scan business cards).</li>
                    <li>Change of mind after a successful purchase.</li>
                    <li>Inability to use the app due to user-side hardware restrictions or poor internet connectivity.</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* SECTION 5 */}
            <section className="space-y-3">
              <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                <span className="w-7 h-7 rounded-xl bg-blue-500/10 text-blue-600 flex items-center justify-center font-black text-xs">5</span>
                How to Request a Refund
              </h2>
              <div className="pl-9 space-y-3 text-sm text-slate-600">
                <p>If you believe you are eligible for a refund based on the criteria above, please contact our support team within 7 days of the transaction.</p>
                <div className="p-4 bg-blue-50 border border-blue-200/80 rounded-2xl space-y-2">
                  <p className="font-bold text-slate-900 text-xs uppercase tracking-wider">Please include the following details in your request:</p>
                  <ul className="list-disc pl-5 space-y-1">
                    <li>Your registered ExpoLead email address.</li>
                    <li>The Razorpay Payment ID or Order ID (found in your receipt).</li>
                    <li>A brief description of the issue.</li>
                  </ul>
                  <div className="pt-2 border-t border-blue-200/60 flex items-center gap-2 text-slate-900 font-bold text-xs">
                    <Mail className="w-4 h-4 text-blue-600" />
                    <span>Send refund requests to:</span>
                    <a href="mailto:expolead@thensgroup.in" className="text-blue-600 underline">expolead@thensgroup.in</a>
                  </div>
                </div>
              </div>
            </section>

          </div>
        </main>
      </div>

      {/* FOOTER */}
      <footer className="py-6 text-center text-xs font-semibold text-slate-400 border-t border-slate-200/80 bg-white/50 backdrop-blur-md">
        <p>Copyright &copy; {new Date().getFullYear()} NS Engineers. All rights reserved.</p>
      </footer>
    </div>
  );
}